// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { GridApi, RowDataChangedEvent, GridReadyEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { waste, WasteContainerDO } from '../../requests';

import WasteEdit from '../forms/waste/Edit';
import Graph from '../forms/waste/Graph';

import WasteUnload from './waste/WasteUnload';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  marked: number;

  data: WasteContainerDO[];
}

export default class Waste extends React.Component<Props, State> {
  private tabId: any;
  private buttons: TableButtonsConfig;
  private mounted: boolean;
  private gridApi: GridApi | undefined;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      // toolbar
      add: true,
      custom: [true],
      // actions
      edit: true,
      del: true,
      graph: true,
      list: true,
      toggleItem: true,
      labels: {
        graph: 'Unloads',
        list: 'Unloads',
        custom: ['Unloads']
      }
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,
      marked: 0,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    this.loadingStart();
    waste<WasteContainerDO[]>('list')
      ?.then((resp: WasteContainerDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
            marked: 0,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public countSelected = (): void => {
    let marked: number = 0;
    if (this.gridApi) {
      marked = this.gridApi.getSelectedNodes().length;
    }
    this.setState({
      update: window.getStr(),
      marked,
    });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'WE';
    const title: string = rowId ? `Waste-Container Edit: ${rowId}` : 'Waste-Container New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: WasteEdit,
      props: {
        rowId,
        report: 'WASTE CONTAINERS',
        title: 'WasteContainers',
        tabId: this.tabId,
        notify: this.notify,
        wasteContainers: this.state.data,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (value: WasteContainerDO): void => {
    window.store.dispatch(removeTab('WE'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: WasteContainerDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      waste('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public onList = (value: WasteContainerDO): void => {
    window.store.dispatch(removeTab('WD'));
    const rowId: number = value.id;
    const tabId: string = 'WD';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: `Container Unloads: ${rowId}`,
      component: WasteUnload,
      props: {
        rowId,
        report: 'CONTAINER UNLOADS ',
        title: 'Container Unloads',
        tabId: this.tabId,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public onCustom = (): void => {
    window.store.dispatch(removeTab('WD'));
    const tabId: string = 'WD';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Total Unloads',
      component: WasteUnload,
      props: {
        tabId: this.tabId,
        report: 'TOTAL UNLOADS',
        title: 'Total Unloads',
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public onGraph = (value: WasteContainerDO): void => {
    window.store.dispatch(removeTab('GE'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadGraph(rowId);
    }, 0);
  };

  public loadGraph = (rowId: number): void => {
    const tabId: string = 'GE';
    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Unloads Graph',
      component: Graph,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'Unloads Graph',
        title: 'Unloads Graph',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public onGridReady = (event: GridReadyEvent): void => {
    // map the api
    this.gridApi = event.api;
  };

  public render(): JSX.Element {
    const { marked, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      minUnload: 'MIN. Unload',
      maxWeight: 'MAX. Weight',
      minTemp: 'MIN. Temp',
      maxTemp: 'MAX. Temp',
      active: 'Active',
      date: 'Date',
      type: 'Type',
      code: 'Code',
      name: 'Name',
      company: 'Company',
      model: 'Model',
      size: 'Size'
    };
    const columnsToHide: string[] = [
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'center' },
      minUnload: { textAlign: 'right' },
      maxWeight: { textAlign: 'right' },
      minTemp: { textAlign: 'right' },
      maxTemp: { textAlign: 'right' },
      active: { textAlign: 'center' },
      date: { textAlign: 'center' },
      type: { textAlign: 'left' },
      code: { textAlign: 'right' },
      name: { textAlign: 'left' },
      company: { textAlign: 'left' },
      model: { textAlign: 'left' },
      size: { textAlign: 'right' },
      actionButtons: { textAlign: 'center' },
    };
    const selectRowProps: Record<string, string> = {
      mode: 'checkbox',
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        minWidth: 120,
      },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <div className="col-6 wdna-padding">
          <span>
            <b>{`Total Rows Selected: ${marked}`}</b>
          </span>
        </div>
        <Table
          data={this.state.data || []}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          customColumnsDefs={customColumnsDefs}
          onAdd={this.onEdit}
          onCustom={[this.onCustom]}
          onEdit={this.onEdit}
          onDelete={this.onDelete}
          onGraph={this.onGraph}
          onList={this.onList}
          // TODO: review ??
          // selectRow={selectRowProps}
          loading={this.state.loading}
          rowDataChanged={this.rowDataChanged}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.countSelected}
          selection="multiple"
        />
      </div>
    );
  }
}
